/**
 * Order Scheduler — Product-page integration
 * -------------------------------------------
 * 1. Populates hidden line-item property fields (Order Type, Date, Time)
 *    from sessionStorage before add-to-cart.
 * 2. Validates day-tag availability: if the user lands on a product page
 *    with a selected date that doesn't match the product's day tags,
 *    show an error and disable the add-to-cart button.
 * 3. If no date is selected and user clicks Add to Cart, intercept and
 *    open the popup scheduler modal.
 * 4. Properly handles products tagged with multiple days (e.g. Thursday,Saturday).
 */
(function () {
  'use strict';

  var OS = window.OrderScheduler;
  if (!OS) return;

  function init() {
    var productForms = document.querySelectorAll('product-form[data-day-tags]');
    if (!productForms.length) return;              // nothing to gate — let the guard open it
    /* Tells the inline fail-open guard in product-add-to-cart.liquid to stand
       down. The button now ships `disabled`, so that guard exists to re-open it
       if this script never runs. Set it BEFORE setup so a throw mid-setup still
       leaves the gate owning the button rather than both fighting over it. */
    window.__osGateActive = true;
    productForms.forEach(function (pf) { setupForm(pf); });
  }

  function setupForm(productForm) {
    var dayTags = (productForm.getAttribute('data-day-tags') || '').trim();
    var sectionId = productForm.getAttribute('data-section');
    var gateEl = document.getElementById('os-product-gate-' + sectionId);
    /* Both add-to-cart controls, not just the main one. The sticky bar's button
       carries form="<product_form_id>", so it submits the SAME form and was a
       second way past the gate. Only buttons marked data-os-gate="pending" are
       ever touched — sold-out and coming-soon buttons don't carry it, so this
       can never enable something that isn't for sale. */
    var addBtns = [];
    (function () {
      var main = productForm.querySelector('#AddToCart, .single-add-to-cart-button');
      if (main) addBtns.push(main);
      var sticky = document.getElementById('AddToCartSticky');
      if (sticky && addBtns.indexOf(sticky) === -1) addBtns.push(sticky);
    })();
    var addBtn = addBtns[0];   // kept: other code reads this

    /* ── Intercept form submission if no date selected ─────────── */
    /* Bypass for jar cakes — they ship nationally and don't need date selection */
    /* The URL test below rarely fires — Shopify redirects
       /collections/jar-cakes/products/x to /products/x — so the reliable signal
       is the Liquid-side collection check on the form. Both kept: belt and braces. */
    var isJarCakePage = window.location.pathname.indexOf('/collections/jar-cakes') !== -1
      || productForm.getAttribute('data-os-no-schedule') === 'true';
    var forms = productForm.querySelectorAll('form[data-type="add-to-cart-form"]');
    forms.forEach(function (form) {
      form.addEventListener('submit', function (e) {
        if (isJarCakePage) return; // Let jar cakes add to cart freely
        if (!OS.getDate()) {
          e.preventDefault();
          e.stopPropagation();
          // Open the popup scheduler
          if (window.OrderSchedulerUI && window.OrderSchedulerUI.openPopup) {
            window.OrderSchedulerUI.openPopup();
          }
          return false;
        }
        // Validate the session-stored date still satisfies THIS page's lead-time.
        // Bug fix (2026-05-11): sessionStorage persists the date across products,
        // but cake pages need 48h notice while cupcake pages only need 24h. A
        // customer who picked tomorrow for cupcakes and then navigated to a cake
        // PDP would carry the 24h date over and bypass the 48h requirement at
        // add-to-cart. Re-validate here against the current page's `getMinDate()`.
        if (typeof OS.getMinDate === 'function') {
          var storedDate = new Date(OS.getDate());
          storedDate.setHours(0, 0, 0, 0);
          var minDate = OS.getMinDate();
          if (!isNaN(storedDate) && storedDate < minDate) {
            // Date is too soon for this product — clear it and re-prompt.
            OS.clearDate();
            OS.clearTime();
            e.preventDefault();
            e.stopPropagation();
            if (window.OrderSchedulerUI && window.OrderSchedulerUI.openPopup) {
              window.OrderSchedulerUI.openPopup();
            }
            // Optional inline notice — visible only briefly before the popup opens.
            try {
              var msg = document.querySelector('.os-inline-notice') || (function () {
                var el = document.createElement('div');
                el.className = 'os-inline-notice';
                el.style.cssText = 'background:#fff4e5;color:#8c4a00;padding:10px 14px;border-radius:6px;margin:10px 0;font-size:14px;font-weight:600;';
                addBtn && addBtn.parentNode && addBtn.parentNode.insertBefore(el, addBtn);
                return el;
              })();
              msg.textContent = 'Cakes require 48 hours’ notice — please pick a later pickup/delivery date.';
              setTimeout(function () { msg.remove(); }, 6000);
            } catch (err) { /* notice is best-effort */ }
            return false;
          }
        }
        // Validate day-tag availability
        if (dayTags && !OS.isProductAvailableOnSelectedDay(dayTags)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Validate readiness
        if (!OS.isReady()) {
          e.preventDefault();
          e.stopPropagation();
          if (window.OrderSchedulerUI && window.OrderSchedulerUI.openPopup) {
            window.OrderSchedulerUI.openPopup();
          }
          return false;
        }
        // All good — populate fields just before submission
        populateFields();
      });
    });

    /* ── Populate hidden fields ─────────────────────────────────────
       The line-item property inputs are always present in the form. When
       OS.getTime() is empty we DISABLE the time input so it isn't submitted
       at all — otherwise an empty `properties[Pickup/Delivery Time]` reaches
       the order, showing as a confusing blank "Pickup/Delivery Time:" line on
       the order detail page (also confuses Renee/Chris in the Square sync).
       This is defense-in-depth — the gate below should already prevent any
       add-to-cart from happening with empty time, but if some path bypasses
       the gate we still don't pollute the order with an empty property. */
    function populateFields() {
      forms.forEach(function (form) {
        var typeInput = form.querySelector('.os-prop-type');
        var dateInput = form.querySelector('.os-prop-date');
        var timeInput = form.querySelector('.os-prop-time');
        if (typeInput) typeInput.value = OS.getType();
        if (dateInput) dateInput.value = OS.getDate();
        if (timeInput) {
          var t = OS.getTime();
          if (t) {
            timeInput.value = t;
            timeInput.disabled = false;
          } else {
            timeInput.value = '';
            timeInput.disabled = true; // Disabled inputs aren't included in form submission
          }
        }
        /* Per-item date — the one field here that actually posts. Stamps the line
           with the date THIS item was ordered for, so a cart holding two items for
           two different days keeps both. Order Type is deliberately left out: it
           can change at checkout, which is exactly what made the old per-line
           Order Type property go stale. Date and time don't change that way. */
        var neededInput = form.querySelector('.os-prop-needed');
        if (neededInput) {
          var nd = OS.getDate();
          var nt = OS.getTime();
          if (nd) {
            neededInput.value = nt ? (nd + ' \u2022 ' + nt) : nd;
            neededInput.disabled = false;
          } else {
            neededInput.value = '';
            neededInput.disabled = true;
          }
        }
      });
    }

    /* ── Set the add-to-cart button state ──────────────────────────
       The native `disabled` attribute is the load-bearing guard — Shopify's
       <product-form> custom element intercepts CLICKS to do AJAX add-to-cart
       before our form-submit listener can run, so disabling at the form-submit
       layer alone isn't enough. The button must be physically disabled.
       `dataset.osDisabled` is kept in sync for any CSS / theme code that
       reads it. */
    function setAddBtnEnabled(enabled) {
      addBtns.forEach(function (b) {
        // Only gate-owned buttons. Anything without the marker is disabled for
        // its own reason (sold out, coming soon) and must stay that way.
        if (b.getAttribute('data-os-gate') !== 'pending') return;
        if (enabled) {
          b.disabled = false;
          delete b.dataset.osDisabled;
        } else {
          b.disabled = true;
          b.dataset.osDisabled = 'true';
        }
      });
    }

    /* ── Validate day-tag availability + order-info readiness ──────
       Jar cakes ship nationally and don't need a pickup/delivery date or
       time, so we exit early on /collections/jar-cakes/* — the bypass at
       line 36 already lets the form submit through. This early-return keeps
       the button enabled. */
    function validate() {
      if (!gateEl) return;
      if (isJarCakePage) {
        gateEl.style.display = 'none';
        gateEl.innerHTML = '';
        setAddBtnEnabled(true);
        return;
      }
      var selectedDay = OS.getDayName();

      // Case 1: No date selected yet — show prompt and DISABLE button.
      if (!OS.getDate()) {
        gateEl.style.display = '';
        gateEl.className = 'os-product-gate os-product-gate--info';
        gateEl.innerHTML = 'Please select a pickup or delivery date before adding items to your cart.';
        setAddBtnEnabled(false);
        return;
      }

      // Case 1b: Date is set but doesn't satisfy THIS page's lead-time
      // (e.g. customer picked tomorrow on a cupcake page, then navigated to
      // a cake page that needs 48h). Disable the button until they update.
      // Must be physically disabled — Shopify's <product-form> intercepts
      // CLICKS before our submit listener can run, so a submit-level guard
      // doesn't prevent AJAX add-to-cart.
      if (typeof OS.getMinDate === 'function') {
        var storedDate = new Date(OS.getDate());
        storedDate.setHours(0, 0, 0, 0);
        var minDate = OS.getMinDate();
        if (!isNaN(storedDate) && storedDate < minDate) {
          gateEl.style.display = '';
          gateEl.className = 'os-product-gate os-product-gate--warn';
          var minStr = minDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
          gateEl.innerHTML = 'This product requires <strong>48 hours’ notice</strong>. Please update your pickup or delivery date to <strong>' + minStr + '</strong> or later.';
          setAddBtnEnabled(false);
          return;
        }
      }

      // Case 2: Product has day tags and selected day doesn't match.
      if (dayTags && !OS.isProductAvailableOnSelectedDay(dayTags)) {
        var availDays = OS.getProductAvailableDays(dayTags);
        var dayListText = availDays.length > 1
          ? availDays.slice(0, -1).join(', ') + ' and ' + availDays[availDays.length - 1]
          : availDays[0];
        gateEl.style.display = '';
        gateEl.className = 'os-product-gate os-product-gate--error';
        gateEl.innerHTML = 'This flavor is only available on <strong>' + dayListText + '</strong>. ' +
          'You selected <strong>' + selectedDay + '</strong>. Please choose a different date or browse other flavors.';
        setAddBtnEnabled(false);
        return;
      }

      // Case 3: Missing order type or time (required for BOTH Pickup and Delivery).
      // Was previously soft — the message rendered but the button stayed enabled,
      // letting customers add to cart with no time and surfacing a blank
      // "Pickup/Delivery Time:" property on the order. Now hard-disabled.
      if (!OS.isReady()) {
        gateEl.style.display = '';
        gateEl.className = 'os-product-gate os-product-gate--warn';
        var missing = [];
        if (!OS.getType()) missing.push('order type (Pickup or Delivery)');
        if (OS.getType() && !OS.getTime()) missing.push('time slot');
        if (missing.length) {
          gateEl.innerHTML = 'Please select your ' + missing.join(' and ') + ' before adding items to your cart.';
        } else {
          gateEl.style.display = 'none';
        }
        setAddBtnEnabled(false);
        return;
      }

      // All good — hide gate, enable button.
      gateEl.removeAttribute('data-os-default');
      gateEl.style.display = 'none';
      gateEl.innerHTML = '';
      setAddBtnEnabled(true);
    }

    /* ── Run on load and whenever the scheduler changes ──────────── */
    populateFields();
    validate();

    document.addEventListener('order-scheduler:change', function () {
      populateFields();
      validate();
    });

    document.addEventListener('order-scheduler:confirmed', function () {
      populateFields();
      validate();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
